import { useEffect, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button, Input, Spin, Form } from "antd";
import { LoadingOutlined } from "@ant-design/icons";

import useOemData from "../../hooks/useOemData";
import { getUserRole } from "../../hooks/getUserRole";
import usePostEngageLogin from "../../hooks/usePostEngageLogin";
import {
  openConfirmNotification,
  openErrorNotification,
} from "../../utils/toast";
import LoginProfileIcon from "../../../assets/images/vectors/LoginProfileIcon";
import PasswordIcon from "../../../assets/images/vectors/PasswordIcon";

// Constants
const ENGAGE_PORTAL_URL = "https://portal.allegionengage.com/";
const AUTHORIZED_ROLES = [4, 5, 7, 8];
const SESSION_STORAGE_KEY = "engageData";

// Utility functions
const encryptToBase64 = (text) => {
  try {
    return btoa(text);
  } catch (error) {
    console.error("Failed to encrypt to Base64:", error);
    return text;
  }
};

const createEncryptedCredentials = (username, password) => ({
  username: encryptToBase64(username),
  password: encryptToBase64(password),
});

const EngageServerLogin = ({ setIsEngageServerModalOpen }) => {
  const [form] = Form.useForm();
  const navigate = useNavigate();
  const postUrl = `${process.env.REACT_APP_API_URL}/api/engage/get-sites`;

  // Hooks
  const { oemNo, oemData } = useOemData();
  const roleValue = getUserRole();
  const {
    data: postData,
    loading: postLoading,
    error: postError,
    postData: postAction,
  } = usePostEngageLogin(postUrl);

  // Memoized handlers
  const handleSuccessfulLogin = useCallback(() => {
    const username = form.getFieldValue("UserName");
    const password = form.getFieldValue("PassWord");

    const encryptedCredentials = createEncryptedCredentials(username, password);

    sessionStorage.setItem(
      SESSION_STORAGE_KEY,
      JSON.stringify(encryptedCredentials)
    );
    openConfirmNotification("Success");
    form.resetFields();

    if (AUTHORIZED_ROLES.includes(roleValue)) {
      setIsEngageServerModalOpen(false);
      navigate("/engagesitelist");
    }
  }, [form, roleValue, setIsEngageServerModalOpen]);

  const handleLoginError = useCallback((error) => {
    const status = error?.response?.data?.status;
    const hasError = error?.response?.data?.error;

    switch (status) {
      case 400:
        openErrorNotification(
          hasError
            ? "Username or password is incorrect."
            : "Something went wrong"
        );
        break;
      case 500:
        openErrorNotification("Internal server error");
        break;
      default:
        openErrorNotification("Username or password is incorrect.");
        break;
    }
  }, []);

  const handleSubmit = useCallback(
    async (values) => {
      try {
        // Encrypt credentials before sending
        const encryptedValues = {
          UserName: encryptToBase64(values.UserName),
          PassWord: encryptToBase64(values.PassWord),
        };

        await postAction(encryptedValues);
      } catch (error) {
        openErrorNotification("Username or password is incorrect.");
      }
    },
    [postAction]
  );

  // Effects
  useEffect(() => {
    if (postData?.data?.status === 200) {
      handleSuccessfulLogin();
    } else if (postData?.data?.status === 401) {
      openErrorNotification(
        postData?.data?.data?.Message || "Something went wrong"
      );
    }

    if (postError) {
      handleLoginError(postError);
    }
  }, [postData, postError, handleSuccessfulLogin, handleLoginError]);

  // Form validation rules
  const validationRules = {
    username: [
      {
        required: true,
        message: "Username is required.",
      },
    ],
    password: [
      {
        required: true,
        message: "Password is required",
      },
    ],
  };

  return (
    <div className="py-12 px-8 flex flex-col items-center !bg-white">
      {/* Logo Section */}
      <div className="w-full max-w-md flex justify-center">
        <img
          src={require(`../../../oem/${oemNo}/images/mainLogo.png`)}
          alt={oemData?.name}
          className="w-[200px] mx-auto"
        />
      </div>

      {/* Login Form */}
      <>
        <p className="my-5 text-2xl text-center"></p>
        <Form
          form={form}
          autoComplete="off"
          requiredMark={false}
          layout="vertical"
          onFinish={handleSubmit}
          className="w-full max-w-md"
        >
          <div className="flex flex-col gap-y-3">
            {/* Username Field */}
            <Form.Item
              name="UserName"
              className="my-input"
              rules={validationRules.username}
            >
              <Input
                className="h-[38px] !bg-[#EEEEEF]"
                placeholder="Username"
                prefix={<LoginProfileIcon className="mr-2" />}
              />
            </Form.Item>

            {/* Password Field */}
            <Form.Item
              name="PassWord"
              className="my-input"
              rules={validationRules.password}
            >
              <Input.Password
                className="!h-[38px] !bg-[#EEEEEF]"
                placeholder="Password"
                prefix={<PasswordIcon className="mr-2" />}
              />
            </Form.Item>

            {/* Submit Button */}
            <Form.Item className="!mb-0 px-[2px] mt-2">
              <Button
                htmlType="submit"
                className="w-full bg-customColor text-white hover:!bg-white hover:!text-customColor border-none font-medium text-[15px] !rounded !py-4 h-[35px]"
              >
                {postLoading ? (
                  <Spin
                    indicator={
                      <LoadingOutlined
                        spin
                        className="h-4 w-4"
                        style={{ color: "white" }}
                      />
                    }
                  />
                ) : (
                  "Login"
                )}
              </Button>
            </Form.Item>
            {/* Engage Server Link */}
            <div className="flex gap-x-3 items-center justify-center w-full">
              <Link
                to={ENGAGE_PORTAL_URL}
                target="_blank"
                className="text-customColor text-center text-sm font-[Poppins] cursor-pointer underline"
              >
                Create Engage Account
              </Link>
            </div>
          </div>
        </Form>
      </>
    </div>
  );
};

export default EngageServerLogin;
